import { css, SerializedStyles } from '@emotion/core'
import parse from 'html-react-parser'
import * as React from 'react'

import { ctaMainBreakPoint, ctaMainInsightsBreakPoint } from '../../constants/breakPoints'
import { breakpoints } from '../../constants/breakPoints'
import { isDesktop, isMobile } from '../../helpers/common'
import { createTypedTranslation } from '../../helpers/translations'
import { useLanguage } from '../../hooks/useLanguage'
import { InstantTourType } from '../../hooks/useTracking'
import { CTAMainProps } from '../../interfaces/cta'
import Block from '../Block'
import { DemoButton, TourButton } from '../Button'
import Image from '../Image'

interface ButtonBlockProps {
  className?: string
  blackBG?: boolean
  primaryButton?: React.ReactNode
  ghostButton?: React.ReactNode
  columnsTemplate?: string
  instantTourVersion?: boolean
  instantTourType?: InstantTourType
  withoutGhostButton?: boolean
  isCentered?: boolean
  cssStyles?: SerializedStyles
}

const CTAMain: React.FunctionComponent<CTAMainProps & ButtonBlockProps> = ({
  type = 'general',
  title,
  cssStyles,
  primaryButton,
  ghostButton,
  instantTourVersion = false,
  withoutGhostButton,
}) => {
  const { t } = useTranslation()
  const getCtaMainData = (
    type:
      | 'general'
      | 'insights'
      | 'prioritization'
      | 'product-strategy'
      | 'roadmaps'
      | 'objectives'
      | 'capacity-planning'
  ): {
    ctaTitle: string
    imgSrc: string
    instantTourType?: InstantTourType
  } => {
    switch (type) {
      case 'general':
        return {
          ctaTitle: t('title.general'),
          imgSrc: 'cta/general.webp',
        }
      case 'insights':
        return {
          ctaTitle: t('title.insights'),
          imgSrc: 'cta/insights.webp',
        }
      case 'prioritization':
        return {
          ctaTitle: t('title.prioritization'),
          imgSrc: 'cta/prioritization.webp',
          instantTourType: 'prioritization',
        }
      case 'product-strategy':
        return {
          ctaTitle: t('title.productStrategy'),
          imgSrc: 'cta/general.webp',
          instantTourType: 'objectives-okrs',
        }
      case 'roadmaps':
        return {
          ctaTitle: t('title.roadmaps'),
          imgSrc: 'cta/roadmaps.webp',
          instantTourType: 'roadmaps',
        }
      case 'objectives':
        return {
          ctaTitle: t('title.objectives'),
          imgSrc: 'cta/objectives.webp',
          instantTourType: 'objectives-okrs',
        }
      case 'capacity-planning':
        return {
          ctaTitle: t('title.capacityPlanning'),
          imgSrc: 'cta/capacity-planning.webp',
        }
    }
  }
  const { ctaTitle, imgSrc, instantTourType } = getCtaMainData(type)
  // padding & img display is depending on lenght of content
  const shortContent = type !== 'general'

  return (
    <Block
      className={'purple-gradient-bg with-box-shadow'}
      columns={2}
      breakPoint={type === 'insights' ? ctaMainInsightsBreakPoint : ctaMainBreakPoint}
      css={[containerStyle, !!cssStyles && cssStyles]}
    >
      <div css={contentStyle(shortContent)}>
        <h2 className={'heading2 black-text no-margin'}>{parse(title ? title : ctaTitle)}</h2>
        <ButtonBlock
          primaryButton={primaryButton}
          ghostButton={ghostButton}
          withoutGhostButton={withoutGhostButton}
          instantTourVersion={instantTourVersion}
          instantTourType={instantTourType}
        />
      </div>

      <div css={imageStyle(type)}>
        <Image
          src={isMobile(breakpoints.small) ? 'cta/cta-airfocus-coin.webp' : imgSrc}
          alt={'airfocus modular platform'}
        />
      </div>
    </Block>
  )
}

export const ButtonBlock: React.FunctionComponent<ButtonBlockProps> = ({
  className,
  primaryButton,
  ghostButton,
  columnsTemplate,
  withoutGhostButton = false,
  instantTourVersion = false,
  instantTourType,
  isCentered,
  cssStyles,
}) => {
  const isFrPage = useLanguage() === 'fr'

  return (
    <Block
      className={`button-block ${className}`}
      columns={withoutGhostButton ? 1 : 2}
      noBreak
      columnGap={0.5}
      rowGap={1}
      columnsTemplate={columnsTemplate ? columnsTemplate : `repeat(${withoutGhostButton ? 1 : 2}, max-content)`}
      css={[buttonsContainerStyle(isFrPage, isCentered), !!cssStyles && cssStyles]}
    >
      {primaryButton || <DemoButton type={'primary'} />}
      {!withoutGhostButton &&
        (ghostButton || (instantTourVersion && <TourButton type={'ghost'} instantTourType={instantTourType} />))}
    </Block>
  )
}

export default CTAMain

const useTranslation = createTypedTranslation({
  title: {
    general: {
      en: 'Experience the new <br class="hide-on-small hide-on-desktop" /> way of doing product <br class="hide-on-small hide-on-desktop" /> management',
      de: 'Entdecke die <br class="hide-on-small" /> moderne Art des <br class="hide-on-small" /> Produkt<span class="hide-on-small">managements</span><span class="hide-on-medium-large-desktop">-Managements</span>',
      fr: 'Découvrez la nouvelle <br class="hide-on-small" /> façon de gérer vos <br class="hide-on-small" />  produits',
    },
    insights: {
      en: 'All product feedback <br class="hide-on-small" /> in one place',
      de: 'Alles Feedback <br class="hide-on-small" /> an einem Ort',
      fr: 'Tous vos retours <br class="hide-on-small hide-on-desktop" /> produits dans un outil',
    },
    prioritization: {
      en: 'Prioritize with <br class="hide-on-small" /> confidence',
      de: 'Klare <br class="hide-on-small" /> Priorisierung',
      fr: 'Priorisez <br class="hide-on-small" /> sereinement',
    },
    productStrategy: {
      en: 'Create <span class="hide-on-small" >an</span> effective <br class="hide-on-small" /> product strategy',
      de: 'Wirksame <br class="hide-on-small" /> Produktstrategie',
      fr: 'Créez une stratégie <br class="hide-on-small" /> produit efficace',
    },
    roadmaps: {
      en: 'Build great <br class="hide-on-small" /> roadmaps',
      de: 'Richtig gute <br class="hide-on-small" /> Roadmaps',
      fr: 'Créez des superbes <br class="hide-on-small" /> roadmaps',
    },
    objectives: {
      en: 'Connect OKRs with <br class="hide-on-small" /> your roadmap and <br class="hide-on-small" /> product work',
      de: 'Verknüpfe OKRs mit <br class="hide-on-small" /> deiner Roadmap <br class="hide-on-small hide-on-large-desktop" /> und <br class="hide-on-small-medium" /> deinen <br class="hide-on-small hide-on-large-desktop" /> Produkt-Items',
      fr: 'Rattachez vos OKRs <br class="hide-on-small" /> à votre roadmap <br class="hide-on-small" /> et à votre travail <br class="hide-on-small" /> sur le produit',
    },
    capacityPlanning: {
      en: `Plan team capacity & <br class="hide-on-small hide-on-large" /> ${
        isDesktop(breakpoints.medium + 1) && isMobile(ctaMainBreakPoint) ? '<br />' : ''
      } align resources <br class="hide-on-small" /> effectively`,
      de: '',
      fr: '',
    },
  },
})

const containerStyle = css`
  border-radius: 0.5rem;
  margin-top: 8rem;
  column-gap: 10%;
  overflow: hidden;

  @media screen and (max-width: ${breakpoints.large}px) {
    column-gap: 0;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    position: relative;
    margin-top: 6rem;
    overflow: visible;
  }
`

const contentStyle = (shortContent: boolean) => css`
  padding: ${shortContent ? '4rem 0 4rem 4rem' : '3rem 0 3rem 4rem'};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  box-sizing: border-box;

  @media screen and (max-width: ${breakpoints.large}px) {
    ${!shortContent && 'padding: 2rem 0 2rem 3rem'};
  }

  @media screen and (max-width: ${ctaMainBreakPoint}px) {
    ${shortContent && 'padding: 3rem 0 3rem 2.5rem'};
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    padding: 2rem 3rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    padding: 2.5rem 2rem 2rem;
  }
`

const imageStyle = (type: string) => css`
  margin: 0 0.5rem;

  @media screen and (max-width: ${breakpoints.large}px) {
    margin: 0;
  }

  ${type === 'insights' &&
  `
    @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${ctaMainInsightsBreakPoint}px) {
      position: absolute;
      width: 60% !important;
      left: 28rem !important;
    }
  `}

  ${type === 'capacity-planning' &&
  `
    position: absolute;
    width: 450px;
    top: -0.75rem;
    right: 2rem;

    @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoints.medium}px) {
      width: 350px !important;
      right: initial !important;
      left: 27rem !important;
    }
  `}

  @media screen and (max-width: ${ctaMainBreakPoint}px) {
    position: absolute;
    width: 50%;
    right: ${type === 'objectives' ? 0 : '-12%'};
  }

  ${type === 'general' &&
  `
    @media screen and (max-width: ${breakpoints.medium}px) {
      width: 65%;
      right: -30%;
    }
  `}

  ${type === 'objectives' &&
  `
    @media screen and (max-width: ${breakpoints.medium}px) {
      width: 48%;
      left: 23rem;
    }
  `}

  @media screen and (max-width: ${breakpoints.small}px) {
    width: 8rem;
    top: -5rem;
    right: 1.5rem;
    // otherwise the overflow breaks the mobile screen behaviour as it overlaps the right screen border
    transform: translateX(2rem);
  }
`
// styles for the <ButtonBlock /> component
const buttonsContainerStyle = (isFrPage: boolean, isCentered?: boolean) => css`
  margin-top: 2rem;

  ${isCentered &&
  `
    width: fit-content;
  `}

  @media screen and (max-width: ${isFrPage ? breakpoints.large : breakpoints.small}px) {
    grid-template-columns: 1fr !important;
    width: 100%;
  }
`
